import { makeStyles } from 'tss-react/mui';
import {
  darkTokens,
  lightTokens,
  typography,
  spacing,
  radius,
  transitions,
} from '../../common/theme/premiumTokens';

/**
 * useSettingsStyles - Premium Design System Styles for CRUD pages
 *
 * Includes:
 * - Table styles (header, body, rows, cells, hover effects)
 * - Form styles (container, header, content, fields, actions)
 * - Accordion styles (premium glassmorphism)
 * - Button styles (primary, secondary, danger)
 * - StatCard styles (dashboard cards with glassmorphism)
 * - Status indicator colors
 *
 * Supports: Dark/Light mode, Mobile responsiveness
 */
export default makeStyles()((theme) => {
  // Get tokens based on current theme mode
  const isDark = theme.palette.mode === 'dark';
  const tokens = isDark ? darkTokens : lightTokens;

  return {
    // ============================================================================
    // CONTAINER STYLES
    // ============================================================================
    container: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(0, 2),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 1),
      },
    },

    // ============================================================================
    // TABLE STYLES - Premium
    // ============================================================================
    table: {
      marginBottom: theme.spacing(10),
      marginTop: '1%',
      marginLeft: '1%',
      marginRight: '1%',
    },

    tableContainer: {
      background: isDark ? 'rgba(22, 27, 34, 0.75)' : 'rgba(255, 255, 255, 0.75)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
      borderRadius: radius.lg,
      overflow: 'hidden',
      boxShadow: tokens.shadows.md,
    },

    tableHeader: {
      backgroundColor: isDark
        ? 'rgba(0, 212, 170, 0.08)'
        : 'rgba(0, 168, 132, 0.08)',
      '& th': {
        fontWeight: typography.fontWeight.semibold,
        fontSize: typography.fontSize.sm,
        color: tokens.text.primary,
        textTransform: 'uppercase',
        letterSpacing: typography.letterSpacing.wide,
        borderBottom: `1px solid ${tokens.border.default}`,
        padding: theme.spacing(1.5, 2),
      },
    },

    tableBody: {
      '& tr': {
        transition: transitions.colors,
        '&:hover': {
          backgroundColor: tokens.surface.glassHover,
        },
        '&:last-child td': {
          borderBottom: 'none',
        },
      },
    },

    tableRow: {
      '&:nth-of-type(odd)': {
        backgroundColor: isDark
          ? 'rgba(255, 255, 255, 0.02)'
          : 'rgba(0, 0, 0, 0.02)',
      },
    },

    tableRowHover: {
      cursor: 'pointer',
      transition: transitions.all,
      '&:hover': {
        backgroundColor: tokens.accent.hover,
        transform: 'translateX(2px)',
      },
    },

    tableRowSelected: {
      backgroundColor: `${tokens.accent.subtle} !important`,
      borderLeft: `3px solid ${tokens.accent.primary}`,
    },

    tableCell: {
      padding: theme.spacing(1.5, 2),
      fontSize: typography.fontSize.sm,
      color: tokens.text.primary,
      borderBottom: `1px solid ${tokens.border.subtle}`,
      verticalAlign: 'middle',
    },

    tableCellCompact: {
      padding: theme.spacing(1, 1.5),
    },

    columnAction: {
      width: '1%',
      paddingRight: theme.spacing(1),
      whiteSpace: 'nowrap',
    },

    // ============================================================================
    // FORM STYLES - Premium
    // ============================================================================
    formContainer: {
      background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
      borderRadius: radius.lg,
      padding: theme.spacing(3),
      marginBottom: theme.spacing(3),
      boxShadow: tokens.shadows.md,
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
        borderRadius: radius.md,
      },
    },

    formContainerPremium: {
      background: isDark ? 'rgba(22, 27, 34, 0.75)' : 'rgba(255, 255, 255, 0.75)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      borderRadius: radius.xl,
      padding: theme.spacing(4),
      marginBottom: theme.spacing(3),
      boxShadow: tokens.shadows.lg,
      border: `1px solid ${tokens.border.accent}`,
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
        borderRadius: radius.lg,
      },
    },

    formHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(3),
      paddingBottom: theme.spacing(2),
      borderBottom: `1px solid ${tokens.border.subtle}`,
    },

    formHeaderPremium: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(3),
      paddingBottom: theme.spacing(2),
      borderBottom: `1px solid ${tokens.border.accent}`,
      '& svg': {
        color: tokens.accent.primary,
        fontSize: 28,
      },
    },

    formTitle: {
      flexGrow: 1,
      fontWeight: typography.fontWeight.semibold,
      fontSize: typography.fontSize.xl,
      color: tokens.text.primary,
    },

    formContent: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        gap: theme.spacing(2),
      },
    },

    formSection: {
      gridColumn: '1 / -1',
      marginBottom: theme.spacing(3),
    },

    formField: {
      marginBottom: theme.spacing(2),
      '& .MuiInputBase-root': {
        backgroundColor: tokens.surface.glass,
        borderRadius: radius.md,
        transition: transitions.all,
        '&:hover': {
          backgroundColor: tokens.surface.glassHover,
        },
        '&.Mui-focused': {
          backgroundColor: tokens.surface.glassHover,
          boxShadow: `0 0 0 2px ${tokens.accent.glow}`,
        },
      },
      '& .MuiInputLabel-root': {
        color: tokens.text.secondary,
        '&.Mui-focused': {
          color: tokens.accent.primary,
        },
      },
    },

    formActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(2),
      marginTop: theme.spacing(3),
      gridColumn: '1 / -1',
      paddingTop: theme.spacing(2),
      borderTop: `1px solid ${tokens.border.subtle}`,
    },

    // ============================================================================
    // ACCORDION STYLES - Premium
    // ============================================================================
    accordionPremium: {
      background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderRadius: `${radius.lg} !important`,
      marginBottom: theme.spacing(2),
      overflow: 'hidden',
      boxShadow: tokens.shadows.sm,
      border: `1px solid ${tokens.border.subtle}`,
      '&:before': {
        display: 'none',
      },
      '&.Mui-expanded': {
        margin: `0 0 ${theme.spacing(2)} 0`,
        boxShadow: tokens.shadows.md,
        border: `1px solid ${tokens.border.accent}`,
      },
    },

    accordionSummaryPremium: {
      backgroundColor: 'transparent',
      minHeight: '56px !important',
      padding: theme.spacing(0, 2),
      '& .MuiAccordionSummary-content': {
        margin: `${theme.spacing(1.5)} 0`,
      },
      '& .MuiTypography-root': {
        fontWeight: typography.fontWeight.medium,
        fontSize: typography.fontSize.md,
        color: tokens.text.primary,
      },
      '& .MuiAccordionSummary-expandIconWrapper': {
        color: tokens.accent.primary,
      },
      '&:hover': {
        backgroundColor: tokens.accent.subtle,
      },
    },

    accordionDetailsPremium: {
      padding: theme.spacing(2, 3, 3),
      backgroundColor: isDark
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.02)',
      borderTop: `1px solid ${tokens.border.subtle}`,
    },

    // ============================================================================
    // BUTTON STYLES - Premium
    // ============================================================================
    buttonPrimary: {
      backgroundColor: tokens.accent.primary,
      color: isDark ? tokens.text.inverse : '#ffffff',
      fontWeight: typography.fontWeight.medium,
      borderRadius: radius.md,
      padding: theme.spacing(1, 3),
      textTransform: 'none',
      boxShadow: tokens.shadows.sm,
      transition: transitions.all,
      '&:hover': {
        backgroundColor: isDark ? '#00e6b8' : '#009973',
        boxShadow: tokens.shadows.glowAccent,
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
      },
      '&:disabled': {
        backgroundColor: tokens.border.default,
        color: tokens.text.muted,
      },
    },

    buttonSecondary: {
      backgroundColor: 'transparent',
      color: tokens.text.primary,
      fontWeight: typography.fontWeight.medium,
      borderRadius: radius.md,
      padding: theme.spacing(1, 3),
      textTransform: 'none',
      border: `1px solid ${tokens.border.default}`,
      transition: transitions.all,
      '&:hover': {
        backgroundColor: tokens.accent.subtle,
        borderColor: tokens.accent.primary,
        color: tokens.accent.primary,
      },
    },

    buttonDanger: {
      backgroundColor: 'transparent',
      color: tokens.semantic.error,
      fontWeight: typography.fontWeight.medium,
      borderRadius: radius.md,
      padding: theme.spacing(1, 3),
      textTransform: 'none',
      border: `1px solid ${tokens.semantic.error}`,
      transition: transitions.all,
      '&:hover': {
        backgroundColor: tokens.semantic.errorBg,
        boxShadow: tokens.shadows.glowAlarm,
      },
    },

    buttonIcon: {
      minWidth: 'auto',
      padding: theme.spacing(1),
      borderRadius: radius.md,
      color: tokens.text.secondary,
      transition: transitions.all,
      '&:hover': {
        backgroundColor: tokens.accent.subtle,
        color: tokens.accent.primary,
      },
    },

    buttons: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      display: 'flex',
      justifyContent: 'space-evenly',
      '& > *': {
        flexBasis: '33%',
      },
      // 2ECOMP: Mobile - espaço extra para BottomMenu
      [theme.breakpoints.down('md')]: {
        marginBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
        paddingBottom: theme.spacing(2),
      },
    },

    // ============================================================================
    // STAT CARD STYLES - Premium Dashboard Cards
    // ============================================================================
    statCard: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(2),
      borderRadius: radius.lg,
      background: isDark
        ? `linear-gradient(135deg, ${tokens.surface.elevated} 0%, ${tokens.surface.base} 100%)`
        : `linear-gradient(135deg, ${tokens.surface.base} 0%, ${tokens.surface.elevated} 100%)`,
      color: tokens.text.primary,
      boxShadow: tokens.shadows.md,
      border: `1px solid ${tokens.border.subtle}`,
      transition: transitions.all,
    },

    statCardPremium: {
      background: isDark ? 'rgba(22, 27, 34, 0.75)' : 'rgba(255, 255, 255, 0.75)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(2.5),
      borderRadius: radius.xl,
      boxShadow: tokens.shadows.md,
      transition: transitions.all,
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: tokens.shadows.lg,
        borderColor: tokens.accent.primary,
      },
      '&:active': {
        transform: 'translateY(0)',
      },
    },

    statCardSuccess: {
      borderLeft: `4px solid ${tokens.semantic.success}`,
      '& $statIcon': {
        color: tokens.semantic.success,
      },
    },

    statCardWarning: {
      borderLeft: `4px solid ${tokens.semantic.warning}`,
      '& $statIcon': {
        color: tokens.semantic.warning,
      },
    },

    statCardError: {
      borderLeft: `4px solid ${tokens.semantic.error}`,
      '& $statIcon': {
        color: tokens.semantic.error,
      },
    },

    statCardInfo: {
      borderLeft: `4px solid ${tokens.semantic.info}`,
      '& $statIcon': {
        color: tokens.semantic.info,
      },
    },

    statIcon: {
      fontSize: 36,
      opacity: 0.9,
      color: tokens.accent.primary,
    },

    statTextContainer: {
      textAlign: 'right',
    },

    statValue: {
      fontWeight: typography.fontWeight.bold,
      fontSize: typography.fontSize['2xl'],
      color: tokens.text.primary,
      lineHeight: typography.lineHeight.tight,
    },

    statLabel: {
      fontSize: typography.fontSize.sm,
      color: tokens.text.secondary,
      marginTop: theme.spacing(0.5),
    },

    // ============================================================================
    // STATUS INDICATOR COLORS
    // ============================================================================
    success: {
      backgroundColor: tokens.semantic.success,
      color: isDark ? tokens.text.inverse : '#ffffff',
    },

    successLight: {
      backgroundColor: tokens.semantic.successBg,
      color: tokens.semantic.success,
      border: `1px solid ${tokens.semantic.success}`,
    },

    warning: {
      backgroundColor: tokens.semantic.warning,
      color: isDark ? tokens.text.inverse : '#ffffff',
    },

    warningLight: {
      backgroundColor: tokens.semantic.warningBg,
      color: tokens.semantic.warning,
      border: `1px solid ${tokens.semantic.warning}`,
    },

    error: {
      backgroundColor: tokens.semantic.error,
      color: '#ffffff',
    },

    errorLight: {
      backgroundColor: tokens.semantic.errorBg,
      color: tokens.semantic.error,
      border: `1px solid ${tokens.semantic.error}`,
    },

    secondary: {
      backgroundColor: tokens.semantic.neutral,
      color: '#ffffff',
    },

    secondaryLight: {
      backgroundColor: tokens.semantic.neutralBg,
      color: tokens.semantic.neutral,
      border: `1px solid ${tokens.semantic.neutral}`,
    },

    info: {
      backgroundColor: tokens.semantic.info,
      color: '#ffffff',
    },

    infoLight: {
      backgroundColor: tokens.semantic.infoBg,
      color: tokens.semantic.info,
      border: `1px solid ${tokens.semantic.info}`,
    },

    // ============================================================================
    // SEARCH AND FILTER STYLES
    // ============================================================================
    searchContainer: {
      background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
      borderRadius: radius.lg,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      flexWrap: 'wrap',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
      },
    },

    searchInput: {
      flex: 1,
      minWidth: 200,
      '& .MuiInputBase-root': {
        backgroundColor: tokens.surface.glass,
        borderRadius: radius.md,
        '&:hover': {
          backgroundColor: tokens.surface.glassHover,
        },
        '&.Mui-focused': {
          boxShadow: `0 0 0 2px ${tokens.accent.glow}`,
        },
      },
    },

    filterChip: {
      backgroundColor: tokens.surface.glass,
      border: `1px solid ${tokens.border.subtle}`,
      borderRadius: radius.full,
      transition: transitions.all,
      '&:hover': {
        backgroundColor: tokens.accent.subtle,
        borderColor: tokens.accent.primary,
      },
      '&.MuiChip-colorPrimary': {
        backgroundColor: tokens.accent.subtle,
        borderColor: tokens.accent.primary,
        color: tokens.accent.primary,
      },
    },

    // ============================================================================
    // LOADING AND EMPTY STATES
    // ============================================================================
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 200,
      color: tokens.accent.primary,
    },

    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(6),
      color: tokens.text.muted,
      textAlign: 'center',
      '& svg': {
        fontSize: 64,
        marginBottom: theme.spacing(2),
        opacity: 0.5,
      },
    },

    // ============================================================================
    // MISC / BACKWARDS COMPATIBILITY
    // ============================================================================
    details: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      paddingBottom: theme.spacing(3),
    },

    verticalActions: {
      display: 'flex',
      flexDirection: 'column',
    },

    dateInput: {
      // Hide native date picker icon in WebKit browsers
      '& input[type="date"]::-webkit-calendar-picker-indicator': {
        display: 'none',
      },
      '& input[type="date"]::-webkit-clear-button': {
        display: 'none',
      },
      '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
        display: 'none',
      },
      '& input[type="datetime-local"]::-webkit-clear-button': {
        display: 'none',
      },
      // Firefox
      '& input[type="date"]::-moz-focus-inner': {
        border: 0,
      },
      '& input[type="datetime-local"]::-moz-focus-inner': {
        border: 0,
      },
    },

    // ============================================================================
    // PAGINATION STYLES
    // ============================================================================
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(2),
      '& .MuiPaginationItem-root': {
        color: tokens.text.primary,
        borderColor: tokens.border.default,
        '&:hover': {
          backgroundColor: tokens.accent.subtle,
        },
        '&.Mui-selected': {
          backgroundColor: tokens.accent.primary,
          color: isDark ? tokens.text.inverse : '#ffffff',
          '&:hover': {
            backgroundColor: isDark ? '#00e6b8' : '#009973',
          },
        },
      },
    },

    // ============================================================================
    // DIALOG STYLES - Premium
    // ============================================================================
    dialogPremium: {
      '& .MuiDialog-paper': {
        background: isDark ? 'rgba(13, 17, 23, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)'}`,
        borderRadius: radius.xl,
        boxShadow: tokens.shadows.xl,
      },
    },

    dialogTitle: {
      padding: theme.spacing(3),
      borderBottom: `1px solid ${tokens.border.subtle}`,
      '& .MuiTypography-root': {
        fontWeight: typography.fontWeight.semibold,
        fontSize: typography.fontSize.xl,
      },
    },

    dialogContent: {
      padding: theme.spacing(3),
    },

    dialogActions: {
      padding: theme.spacing(2, 3, 3),
      borderTop: `1px solid ${tokens.border.subtle}`,
      gap: theme.spacing(1),
    },
  };
});
